import { END_TO_END_CHANNEL } from "../../../constants/treeConstants";
import { strDelimited } from "../../helper/split";
import { IC, SERDES_TYPES } from "../../PCBHelper";
import { getUsableComps } from "../channel";
import channelSetupInfo from "../channel/channelInfo";
import { controllerModelTypes, deviceModelTypes, HspiceSignalConfig } from "../simulation";
import { handleComponents } from "../simulation/IntegratedHspiceConfig";

function getICCompName(channelInfo = {}, compName) {
  const components = getUsableComps(channelInfo);
  const ICComps = components.filter(item => SERDES_TYPES.includes(item.type) && (!compName || item.name !== compName));
  const ICComp = ICComps.find(item => item.type === IC) || (ICComps[0] || null);
  return ICComp ? ICComp.name : "";
}

function updateHspiceSignal({
  hspiceSignals,
  connIndex,
  pcbConn,
  changeType,
  comp,
  connections,
  channelInfo = {},
  updateFunctionName,
  isDiffChannel,
  oldPcbConn = {},
  oldConnections = [],
  oldChannelInfo = {},
  oldComp
}) {
  const connectionId = connIndex === 0 ? pcbConn.nextConnectionId : pcbConn.prevConnectionId;
  const connection = connections.find(item => item.CONNECTION_ID === connectionId);
  if (!connection) {
    return hspiceSignals;
  }
  const signal_connections_map = connection.connection.signal_connections_map || [];
  const signalIndex = connIndex === 0 ? "channel1_signal" : "channel2_signal";

  let old_signal_connections_map = [];
  if (isDiffChannel) {
    const connectionId = connIndex === 0 ? oldPcbConn.nextConnectionId : oldPcbConn.prevConnectionId;
    const connection = oldConnections.find(item => item.CONNECTION_ID === connectionId);
    old_signal_connections_map = connection.connection.signal_connections_map || [];
  }

  // update pairs and pinModels
  return hspiceSignals.map(signal => {
    const findSignal = signal_connections_map.find(item => item.name === signal.signalName);
    if (!findSignal) {
      return signal;
    }
    const { components = [], signals = [] } = channelInfo.content || {};
    const signalKey = findSignal[signalIndex];
    const _signalInfo = signals.find(item => item.name === signalKey) || {};
    const pinsInfo = handleComponents(
      components,
      _signalInfo,
      changeType === 'controller' ? comp : null,
      changeType === 'controller' ? null : comp
    );
    const pins = changeType === 'controller' ? pinsInfo.controllerPins : pinsInfo.devicePins;

    let oldPins = [];
    if (isDiffChannel) {
      const old_findSignal = old_signal_connections_map.find(item => item.name === signal.signalName);
      if (old_findSignal) {
        const { components = [], signals = [] } = oldChannelInfo.content || {};
        const old_signalKey = old_findSignal[signalIndex];
        const old_signalInfo = signals.find(item => item.name === old_signalKey) || {};
        const old_pinsInfo = handleComponents(
          components,
          old_signalInfo,
          changeType === 'controller' ? oldComp : null,
          changeType === 'controller' ? null : oldComp
        );
        oldPins = changeType === 'controller' ? old_pinsInfo.controllerPins : old_pinsInfo.devicePins;
      }
    }
    const eleNames = changeType === 'controller' ? controllerModelTypes : deviceModelTypes;
    const hspiceSignal = new HspiceSignalConfig({ ...signal });
    for (const eleName of eleNames) {
      switch (updateFunctionName) {
        case "checkModel":
          hspiceSignal.checkModel(eleName, pins, _signalInfo);
          break;
        case "replacePinInModel":
          hspiceSignal.replacePinInModel(eleName, { pins }, _signalInfo);
          break;
        case "replacePinInModelsByDiffChannel":
          hspiceSignal.replacePinInModelsByDiffChannel(eleName, pins, oldPins, _signalInfo);
          break;
        default:
          break;
      }
    }

    return hspiceSignal;
  })
}

// select new channel
function clearEndToEndHspiceComponent({
  pcbConn,
  hspiceConfig,
  prevChannelId,
  connections,
  connIndex,
  channelInfo,
}) {
  let _hspiceConfig = { ...hspiceConfig };

  const comp = getICCompName(channelInfo);
  let changeType = "";
  for (const type of ["controller", "device"]) {
    const otherType = type === "controller" ? "device" : "controller";
    if (!_hspiceConfig[`${type}Channel`]) {
      continue;
    }
    const otherChannelId = (_hspiceConfig[`${otherType}Channel`] || {}).channelId;
    if ((!_hspiceConfig[`${type}Channel`].channelId || _hspiceConfig[`${type}Channel`].channelId === prevChannelId)
      && ((prevChannelId && _hspiceConfig[`${type}Channel`].channelId) || (otherChannelId !== pcbConn.channelId && pcbConn.channelId))) {
      _hspiceConfig[type] = comp;
      _hspiceConfig[`${type}Channel`] = { designId: pcbConn.designId, designName: pcbConn.designName, channelId: pcbConn.channelId };
      changeType = type;
      break;
    }
  }

  if (changeType) {
    _hspiceConfig.signals = updateHspiceSignal({
      hspiceSignals: _hspiceConfig.signals,
      connIndex,
      pcbConn,
      changeType,
      comp,
      connections,
      channelInfo,
      updateFunctionName: "checkModel"
    })
  }

  return _hspiceConfig;
}

function updateEndToEndHspiceCompByConnection({
  hspiceConfig,
  pcbConn,
  compName,
  channelInfo = {},
  connIndex,
  connections = []
}) {
  let _hspiceConfig = { ...hspiceConfig };

  const comp = compName ? getICCompName(channelInfo, compName) : null
  let changeType = "";
  for (const type of ["controller", "device"]) {
    if (_hspiceConfig[`${type}Channel`] && _hspiceConfig[`${type}Channel`].channelId === pcbConn.channelId) {
      _hspiceConfig[type] = comp;
      changeType = type;
      break;
    }
  }
  if (changeType) {
    _hspiceConfig.signals = updateHspiceSignal({
      hspiceSignals: _hspiceConfig.signals,
      connIndex,
      pcbConn,
      changeType,
      comp,
      connections,
      channelInfo,
      updateFunctionName: "replacePinInModel"
    })
  }

  return _hspiceConfig;
}

// add empty signal
function addSignalInHspiceConfig({ hspiceConfig, signalName }) {
  if (!hspiceConfig.signals) return hspiceConfig;

  const adsSignal = new HspiceSignalConfig({
    type: END_TO_END_CHANNEL,
    signalName,
    controllerPins: [],
    devicePins: [],
    signalInfo: {},

  })

  return { ...hspiceConfig, signals: [...hspiceConfig.signals, adsSignal] }
}

// add PCB，One side of the PCB has been cleared, and the associated component, channelInfo, and model data need to be cleared
// delete PCB：
//   1. There is a new PCB for the connection of the hspiceConfig. (Replace the pin and net with netType, and keep the rest of the configuration unchanged.) 
//   2. There is no PCB as a connection for the hspiceConfig
function updateEndToEndHspiceCompByPCB({
  hspiceConfig,
  delPCB,
  newIndex,
  newPCB = {},
  channelInfo = {},
  connections = [],
  delChannelInfo = {},
  oldConnections = []
}) {
  if (!delPCB || !delPCB.channelId) {
    return hspiceConfig;
  }
  let _hspiceConfig = { ...hspiceConfig };

  const connectionKey = newIndex === 0 ? "nextConnectionId" : "prevConnectionId";
  const connectionId = newPCB[connectionKey];
  const connection = connections.find(item => item.CONNECTION_ID === connectionId) || { connection: { connector1: {}, connector2: {} } };
  const compName = newIndex === 0 ? connection.connection.connector1.component : connection.connection.connector2.component;
  const comp = getICCompName(channelInfo, compName);

  let changeType = "", oldComp = "";
  for (const type of ["controller", "device"]) {
    if (_hspiceConfig[type] && _hspiceConfig[`${type}Channel`] && _hspiceConfig[`${type}Channel`].designId === delPCB.designId) {
      oldComp = _hspiceConfig[type];
      _hspiceConfig[type] = comp;
      _hspiceConfig[`${type}Channel`] = { designId: newPCB.designId || "", designName: newPCB.designName || "", channelId: newPCB.channelId || "" }
      changeType = type;
      break;
    }
  }

  // update pairs and pinModels
  _hspiceConfig.signals = updateHspiceSignal({
    hspiceSignals: _hspiceConfig.signals,
    connIndex: newIndex,
    pcbConn: newPCB,
    changeType,
    comp,
    connections,
    channelInfo,
    updateFunctionName: "replacePinInModelsByDiffChannel",
    isDiffChannel: Boolean(newPCB.channelId),
    oldPcbConn: delPCB,
    oldConnections,
    oldChannelInfo: delChannelInfo,
    oldComp
  })
  return _hspiceConfig;
}

// Channel comp Type -> Ignore
function delEndToEndHspiceCompByIgnoreComp({
  hspiceConfig,
  component,
  channelId
}) {
  let _hspiceConfig = { ...hspiceConfig };
  let changeType = "";
  for (const type of ["controller", "device"]) {
    if (_hspiceConfig[type] === component && _hspiceConfig[`${type}Channel`] && _hspiceConfig[`${type}Channel`].channelId === channelId) {
      _hspiceConfig[type] = "";
      _hspiceConfig[`${type}Channel`] = { designId: "", designName: "", channelId: "" }
      changeType = type;
      break;
    }
  }

  _hspiceConfig.signals = _hspiceConfig.signals.map(signal => {
    const hspiceSignal = new HspiceSignalConfig({ ...signal });
    hspiceSignal.clearModels(changeType === "controller" ? controllerModelTypes : deviceModelTypes);
    return hspiceSignal;
  })

  return _hspiceConfig;
}

// deletedICConnComps: {pin,net,name}
function updateEndToEndHspiceConfigBySelectNets({ hspiceConfig, signalName, designId, channelId, components, signalInfo }) {
  let changeType = "", comp = "";
  for (const type of ["controller", "device"]) {
    if (hspiceConfig[`${type}Channel`] && hspiceConfig[`${type}Channel`].designId === designId && hspiceConfig[`${type}Channel`].channelId === channelId) {
      changeType = type;
      comp = hspiceConfig[type];
      break;
    }
  }

  let hspiceSignals = [...hspiceConfig.signals];
  const hspiceSignalIndex = hspiceConfig.signals.findIndex(item => item.signalName === signalName);
  if (hspiceSignalIndex < 0) {
    return hspiceConfig;
  }
  if (components.find(item => item.name === hspiceConfig[changeType])) { // comp exist
    const pinsInfo = handleComponents(
      components,
      signalInfo,
      changeType === 'controller' ? comp : null,
      changeType === 'controller' ? null : comp
    );
    const pins = changeType === 'controller' ? pinsInfo.controllerPins : pinsInfo.devicePins;

    const eleNames = changeType === 'controller' ? controllerModelTypes : deviceModelTypes;
    const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
    for (const eleName of eleNames) {
      _item.checkModel(eleName, pins, signalInfo);
    }
    hspiceSignals[hspiceSignalIndex] = _item;
  } else { // clear
    hspiceConfig[changeType] = "";
    hspiceConfig[`${changeType}Channel`] = { designId: "", designName: "", channelId: "" }
    const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
    _item.clearModels(changeType === "controller" ? controllerModelTypes : deviceModelTypes);
    hspiceSignals[hspiceSignalIndex] = _item;
  }
  return {
    ...hspiceConfig,
    signals: hspiceSignals
  }
}

// channel select pin
function updateEndToEndHSPICEPin({ hspiceConfig, component, channelId, newPin, prevPin, signals }) {
  let changeType = "";
  for (const type of ["controller", "device"]) {
    if (hspiceConfig[type] === component && hspiceConfig[`${type}Channel`] && hspiceConfig[`${type}Channel`].channelId === channelId) {
      changeType = type;
      break;
    }
  }
  if (!changeType) return hspiceConfig;

  const signalName = (signals.find(signal => signal[`${changeType === 'controller' ? 'channel1_signal' : 'channel2_signal'}`] === newPin.signal) || {}).name;
  let hspiceSignals = [...hspiceConfig.signals];
  const hspiceSignalIndex = hspiceConfig.signals.findIndex(item => item.signalName === signalName);
  if (hspiceSignalIndex > -1) {
    const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
    _item.checkPin(newPin, prevPin);
    hspiceSignals[hspiceSignalIndex] = _item;
  }

  return {
    ...hspiceConfig,
    signals: hspiceSignals
  }
}

function updateEndToEndHspiceCompBySignal({ hspiceConfig, record, dataIndex, pcbChannel, lastIndex }) {
  const connIndex = strDelimited(dataIndex, "_", { returnIndex: 1 });

  if (connIndex !== "0" && connIndex !== String(lastIndex)) {
    return hspiceConfig;
  }

  let changeType = "", comp = "";
  for (const type of ["controller", "device"]) {
    if (hspiceConfig[`${type}Channel`] && hspiceConfig[`${type}Channel`].designId === pcbChannel.designId) {
      changeType = type;
      comp = hspiceConfig[type];
      break;
    }
  }
  if (!changeType) return hspiceConfig;

  const hspiceSignalIndex = hspiceConfig.signals.findIndex(item => item.signalName === record.name);
  let hspiceSignals = [...hspiceConfig.signals];
  const content = channelSetupInfo.getContent(pcbChannel.channelId);
  const components = getUsableComps({ content }),
    signals = content.signals;

  const signalInfo = signals.find(item => item.name === record[dataIndex])
  const pinsInfo = handleComponents(
    components,
    signalInfo,
    changeType === 'controller' ? comp : null,
    changeType === 'controller' ? null : comp
  );
  const pins = changeType === 'controller' ? pinsInfo.controllerPins : pinsInfo.devicePins;

  const eleNames = changeType === 'controller' ? controllerModelTypes : deviceModelTypes;
  const _item = new HspiceSignalConfig(hspiceSignals[hspiceSignalIndex]);
  for (const eleName of eleNames) {
    _item.checkModel(eleName, pins, signalInfo);
  }
  hspiceSignals[hspiceSignalIndex] = _item;
  return {
    ...hspiceConfig,
    signals: hspiceSignals
  }
}

export {
  clearEndToEndHspiceComponent,
  updateEndToEndHspiceCompByConnection,
  addSignalInHspiceConfig,
  updateEndToEndHspiceCompByPCB,
  delEndToEndHspiceCompByIgnoreComp,
  updateEndToEndHSPICEPin,
  updateEndToEndHspiceConfigBySelectNets,
  updateEndToEndHspiceCompBySignal
}